import { useCallback, useRef } from "react";
import { structuralSharingWithDecimals } from "src/lib/structuralSharing";

/**
 * Returns the previous reference of `value` when the new value is deep-equal
 * to the previous one. Useful for stabilizing array/object outputs of
 * `useMemo` chains so downstream memoized consumers can bail out.
 *
 * Uses `structuralSharingWithDecimals` so `Decimal` / `Date` instances are
 * treated as value-equal and class instances (from `class-transformer`) are
 * recursed into like plain objects.
 */
export function useStableValue<T>(value: T): T {
  const ref = useRef<T>(value);
  ref.current = structuralSharingWithDecimals(ref.current, value);
  return ref.current;
}

/**
 * Wraps a React Query `select` function so that it returns the previous
 * reference when the new result is structurally equal to the old one.
 * Subtrees that didn't change also keep their old references, which helps
 * downstream memoized consumers bail out more often than a simple all-or-
 * nothing deep-equal check would.
 */
export function useStableSelect<TData, TSelected>(
  selectFn: (data: TData) => TSelected
): (data: TData) => TSelected {
  const prevRef = useRef<TSelected | undefined>(undefined);

  return useCallback(
    (data: TData) => {
      const next = selectFn(data);
      prevRef.current = structuralSharingWithDecimals(prevRef.current, next);
      return prevRef.current;
    },
    [selectFn]
  );
}
